import { resolvePlanningBufferPct, formatPlanningBufferLabel } from '@/utils/planningBuffer';
import { Technician, Ticket } from '@/types';
import { EFFICIENCY_NAMES } from '@/lib/efficiencyNames'; // CORE-G GAP-005: one name, one meaning
import { calculateWallMinutes } from '@/utils/canonicalTimeMath';
// Phase C — canonical workload reducer. The header workload number must
// match what the ticket cards render (Bucket 5 §5.1/§5.4). Held-in-
// parking / non-bay holds contribute 0 minutes; active running tickets
// contribute their live projected occupancy; unstarted tickets
// contribute Tech OptiTime; bay-blocking paused tickets contribute
// Tech OptiTime + bay-blocking pause time.
import { getDerivedTicketTime, sumWorkloadMinutes } from '@/utils/derivedTicketTime';
import { sumRemainingHandsOn, headerElapsedFromEvidence } from '@/lib/scheduling/liveTicketCoverage';
import { useTicketWorkSessions } from '@/hooks/useTicketWorkSessions';
import { clock as canonicalClock } from '@/utils/clock';
import { Badge } from '@/components/ui/badge';
import { TicketCard } from '@/components/tickets/TicketCard';
import { Clock, Wrench, GripVertical } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDraggable } from '@dnd-kit/core';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { isTechnicianScheduledPresent, isTechnicianScheduledForDay } from '@/utils/technicianPresence';
// DMC-D3c-1: canonical deleted-state helper. Note: the legacy
// `Ticket` type at src/types/index.ts uses camelCase + does not
// declare `deleted_at`, so the helper falls back to the status check
// for legacy rows. For Supabase rows that carry both reps, the helper
// adds defense-in-depth.
import { isTicketDeleted } from '@/utils/ticketDeletedState';
import { useDate } from '@/contexts/DateContext';
import { format, parse } from 'date-fns';
import { useShopTime } from '@/hooks/useShopTime';
import { fromZonedTime } from 'date-fns-tz';
import { useState, useEffect } from 'react';
// DTC-E: route the "current instant" anchor through the canonical
// clock so the time-clock load and the presence-label computation
// are deterministic under `clock.setFixed(...)` test fixtures. The
// `nowShop.getHours()` reads later in the file already operate on a
// pre-zoned shop-local Date (via `toShopTZ`) — those are class C
// safe and intentionally left alone.
import { clock } from '@/utils/clock';
// TIM-A5: collapse the per-column 30s render-tick (`setInterval(() =>
// forceTimeTick(...), 30000)`) onto the shared minute-tick singleton.
// Pre-A5 every <TechnicianColumn> mounted on the dashboard owned its
// own 30s setInterval whose only job was to bump a discarded state
// value (`forceTimeTick`) to force a re-render so the smart presence
// label (`computePresenceLabel`) could re-evaluate `clock.now()`-
// derived buckets (Pre-Shift / Present / Off-Shift / Lunch / Break /
// etc.). On a 12-tech board that was 12 wall-clock-aligned 30s
// kernels for a render-only side effect with NO DB reads.
//
// Cadence tradeoff (60s vs the pre-A5 30s): boundary-crossing label
// changes (shift start/end, break start/end) flip up to 60s after
// the boundary instead of up to 30s. Acceptable for a passive
// presence-label readout; no user-visible action surface depends on
// sub-minute precision. Hidden-tab pause/catch-up (TIM-A3) inherited
// automatically.
import { useSharedMinuteTimer } from '@/hooks/useSharedTimer';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { formatTimeString } from '@/utils/formatShopTime';
import { getTechDayHours } from '@/utils/shopTime';
// NOV 3: the header bounds each ticket by its own scheduled day's close (as the card does)
import { getTechShiftBoundaryUtc } from '@/utils/shopTime';
import { PausedWorkBadge } from './PausedWorkBadge';
// Bucket 5 §5.5.4 — Slice 0.3b: pure helper that resolves the Awaiting vs
// No-Show Risk split for a scheduled, mid-shift, unclocked technician under
// time-clock-enabled mode. Display-only.
import { getTimeClockUnclockedLabel } from './presenceLabel';
import { ownsInChain } from '@/lib/scheduling/chainOwnership';
// W5 (field test 2026-10-16): multi-bay workspace. ADDITIVE ONLY — when the pure predicate is inactive (every
// ordinary single-bay technician) the column renders byte-identically to before (golden parity test
// tests/unit/dayboard/technicianColumnSingleBayParity.test.tsx, captured at ef27bbce before this edit).
import {
  deriveMultiBayActivity, isMultiBayWorkspaceEnabled, INACTIVE_MULTI_BAY, type OpenUnattendedOperation,
} from '@/lib/dayboard/multiBayActivity';
import { MultiBayTechnicianSections, multiBaySortableIds } from './multibay/MultiBayTechnicianSections';
import { useShopBayNames } from './multibay/useShopBayNames';
import { TechnicianWorkspaceDialog } from './workspace/TechnicianWorkspaceDialog';

interface TechnicianColumnProps {
  technician: Technician;
  tickets: Ticket[];
  onReassignNeeded?: (tickets: Ticket[], technicianName: string) => void;
  onKebabReassign?: () => void;
  compactView?: boolean;
  dragOverTechId?: string | null;
  previewStartTime?: string | null;
  activeTicketId?: string | null;
  hoverIndex?: number;
  activeTicket?: any;
  isDraggable?: boolean;
  highlightedTicketId?: string | null;
  pendingMoveTicketId?: string | null;
  isFirstTechnician?: boolean;
  newOptiQueueTicketIds?: Set<string>;
  // L-3: Lifted from per-column shop_profile fetch — passed once from Dashboard to avoid N queries
  timeClockEnabled?: boolean;
  /**
   * Bucket 5 §5.5.4 — Slice 0.3b: shop_culture.no_show_grace_threshold_min,
   * lifted from per-column shop_profile fetch and passed once from Dashboard
   * (same pattern as `timeClockEnabled`). Display-only in this slice; controls
   * the Awaiting → No-Show Risk label transition for unclocked techs past
   * (shift_start + threshold). When omitted, the helper defaults to 15.
   */
  noShowGraceThresholdMin?: number;
  /** When true, tickets in this column are view-only (no kebab, timer, drag, or edit interactions) */
  readOnly?: boolean;
  /** Called after a job mutation so Dashboard can refresh local state */
  onTicketUpdated?: (ticketId: string) => void;
  /**
   * Slice 5D-12 Batch 2: lookup map of pre-computed advisor bay-hold
   * data, keyed by ticket id. Built ONCE at the Dashboard level by
   * `useBoardAdvisorBayHold` so per-column rendering pays zero query
   * cost. Optional — when omitted, inner TicketCards fall back to
   * Batch 1's lower-bound display.
   */
  advisorBayHoldByTicketId?: Map<string, import('@/hooks/tickets/useBoardAdvisorBayHold').TicketAdvisorBayHoldData> | null;
  /**
   * Phase 7.3: lookup map of pre-computed split label data
   * (`SplitPartLabelData`) keyed by ticket id. Built ONCE at the
   * Dashboard level via `buildSplitPartLabelDataMap(tickets)` so
   * per-column rendering pays zero reconstruction cost. Optional —
   * when omitted, inner SplitLinks fall back to the generic "Split"
   * label.
   */
  splitLabelDataByTicketId?: Map<string, import('@/utils/splitGroupIdentity').SplitPartLabelData> | null;
  /**
   * W5: open unattended operations (machine processes) keyed by ticket id, read ONCE at the Dashboard level
   * (useOpenUnattendedOperations). Optional — when omitted the column never shows an Unattended section.
   */
  unattendedByTicketId?: ReadonlyMap<string, readonly OpenUnattendedOperation[]> | null;
}

/**
 * The technician's Dayboard chain: which tickets belong in this column, in
 * order. Exported so the real rule is what tests exercise (a Completed ticket
 * is never in the column; order is start_time).
 */
// eslint-disable-next-line react-refresh/only-export-components
export const getFilteredTickets = (tickets: Ticket[], techId: string) => {
  return tickets
    .filter(t => {
      // Standard match: tech currently owns the ticket (in their bay or
      // about to be). Unchanged behavior for active work.
      const ownsNow = t.assignedTechId === techId;

      // Parked-but-scheduled: the immunity rule nulls assigned_tech_id on
      // park, so we'd lose the visual association without this fallback.
      // `scheduled_tech_id` is the dispatch plan (migration
      // 20260601160000); it survives the park transition. We only fall
      // back to it when the ticket is currently parked AND has no live
      // assignment — otherwise the active assignment wins. This keeps a
      // checked-in scheduled ticket visible in its planned tech's column
      // with the existing parking-lot visual treatment (see TicketCard's
      // `isInParkingLot` collapsed state).
      // FLOYD-8 — asked of the SHARED rule now. This condition was correct
      // and was also the only correct copy: `useAutoBump` and
      // `globalCascade` each kept their own, and theirs were wrong.
      const parkedScheduledForMe = ownsInChain(
        {
          assigned_tech_id: (t.assignedTechId ?? null) as string | null,
          scheduled_tech_id: ((t as any).scheduled_tech_id ?? null) as string | null,
          parking_lot: ((t as any).parking_lot ?? null) as boolean | null,
          schedule_state_reason: ((t as any).schedule_state_reason ?? null) as string | null,
        },
        techId,
      ) && t.assignedTechId == null;

      if (!ownsNow && !parkedScheduledForMe) return false;

      if (t.status === 'Completed') return false;
      // DMC-D3c-1: canonical deleted-state check (both reps).
      if (isTicketDeleted(t as any)) return false;
      // Codex pause doctrine: only show paused tickets if in-bay in-progress.
      // Doesn't apply to the parked-scheduled fallback — those are by
      // definition NOT in a bay and shouldn't be filtered by this rule.
      if (
        t.status === 'Paused' &&
        !((t as any).actually_started === true && (t as any).bay_id != null) &&
        !parkedScheduledForMe
      ) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      const aTime = new Date((a as any).start_time).getTime();
      const bTime = new Date((b as any).start_time).getTime();
      return aTime - bTime;
    });
};

export const TechnicianColumn = ({ 
  technician, 
  tickets, 
  onReassignNeeded, 
  onKebabReassign,
  compactView = false,
  dragOverTechId,
  previewStartTime,
  activeTicketId,
  hoverIndex = -1,
  activeTicket: activeTicketProp,
  isDraggable = false,
  highlightedTicketId = null,
  pendingMoveTicketId = null,
  isFirstTechnician = false,
  newOptiQueueTicketIds = new Set(),
  // L-3: Received from Dashboard (fetched once per shop, not per column)
  timeClockEnabled: timeClockEnabledProp = false,
  // Bucket 5 §5.5.4 — Slice 0.3b: same Dashboard-lift pattern. Default 15 mirrors
  // shop_culture.no_show_grace_threshold_min's documented default and matches the
  // fallback used in autonomous-optimizer / optimize-board.
  noShowGraceThresholdMin = 15,
  readOnly = false,
  onTicketUpdated,
  advisorBayHoldByTicketId = null,
  splitLabelDataByTicketId = null,
  unattendedByTicketId = null,
}: TechnicianColumnProps) => {
  const { toast } = useToast();
  // DCD-A3: pulled `selectedDayLabel` (canonical shop-local YYYY-MM-DD)
  // alongside `selectedDate`. Date-label derivations now use the
  // canonical string directly instead of `format(selectedDate, 'yyyy-MM-dd')`.
  // `selectedDate` Date object remains for helpers that accept a Date
  // (`isTechnicianScheduledForDay`). The last `getDay()` day-of-week index
  // went with the per-day hours repair: `getTechDayHours` takes the canonical
  // shop-local label, which is also the only shape that gets the weekday right
  // for a shop west of Greenwich.
  const { selectedDate, selectedDayLabel } = useDate();
  const { formatTimeInShopTZ, toShopTZ, shopTimezone } = useShopTime();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isManagerView, role } = useUserRole();
  const techTickets = getFilteredTickets(tickets, technician.id);

  // W5: does actual concurrent activity justify the multi-bay layout? Pure, recomputed every render; never reads
  // bay_assignments. Inactive → everything below renders exactly as it always has.
  const isShopToday = selectedDayLabel === format(toShopTZ(clock.now()), 'yyyy-MM-dd');
  const multiBay = isMultiBayWorkspaceEnabled()
    ? deriveMultiBayActivity({ techTickets: techTickets as never, isShopToday, unattendedByTicketId })
    : INACTIVE_MULTI_BAY;
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const bayNames = useShopBayNames((technician as any).shop_id, multiBay.active);
  // OCT28 WP-2b-2b-i: the multi-bay header measures remaining hands-on work from the recorded work sessions; one read
  // per column with started work, repeated only when a ticket starts / pauses / stops (its row's timer fields change).
  // NOV 3 (Decision 3): every column's workload header reads elapsed WORK from these sessions (never wall time since
  // the first start), so the read is no longer multi-bay only.
  const startedTicketIds = techTickets
    .filter((t) => (t as any).actually_started === true || (t as any).timer_status === 'running' || (t as any).timer_status === 'paused')
    .map((t) => t.id);
  const workEvidence = useTicketWorkSessions(
    startedTicketIds,
    techTickets.map((t) => `${t.id}:${(t as any).timer_status ?? ''}:${(t as any).current_session_id ?? ''}:${(t as any).paused_at ?? ''}`).join('|'),
    startedTicketIds.length > 0,
  );

  // Codex: PausedWorkBadge — compute paused workflow tickets (NOT in-bay in-progress)
  const canSeePausedBadge = role === 'OWNER' || role === 'MANAGER' || role === 'SERVICE_WRITER' || role === 'DISPATCHER';
  const pausedWorkflowTickets = canSeePausedBadge ? tickets
    .filter(t => 
      t.assignedTechId === technician.id &&
      t.status === 'Paused' &&
      !((t as any).actually_started === true && (t as any).bay_id != null)
    )
    .map(t => ({
      id: t.id,
      customerName: (t as any).customer_name || t.customerName,
      vehicleInfo: t.vehicle ? `${t.vehicle.year} ${t.vehicle.make} ${t.vehicle.model}` : undefined,
      pauseReason: (t as any).pause_reason || null,
      pausedAt: (t as any).paused_at || null,
      reactivateAt: (t as any).reactivate_at || null,
    })) : [];
  // THE SELECTED DAY, RESOLVED CANONICALLY.
  //
  // This was `hours?.[dayIndex]` — an index-only lookup. `technicians.hours`
  // is written keyed by day INDEX by the form and by day NAME by the commit
  // wizard and several edge writers, and `getTechDayHours` is the one reader
  // that accepts both. The raw index lookup missed every name-keyed map, fell
  // through to the flat `shiftStart`/`shiftEnd` pair below, and drew a WEEKLY
  // MODE in a column that names a date.
  //
  // Joe D is the case: Monday 07:30–22:00, Tue–Fri 07:30–16:00 derives
  // `shift_end = 16:00`, so his Monday column claimed he goes home at four on
  // the one day he does not.
  const techHours = getTechDayHours(technician as never, selectedDayLabel);
  
  // L-3: Use prop value instead of per-column shop_profile fetch
  const timeClockEnabled = timeClockEnabledProp;
  const [activeClockEntry, setActiveClockEntry] = useState<any>(null);
  const [lastClockEntryToday, setLastClockEntryToday] = useState<any>(null);
  // TIM-A5: subscribe once to the shared 60s singleton. The returned
  // value is unused — `computePresenceLabel` reads `clock.now()`
  // directly each render, and the subscription's `setState` notification
  // is what causes the column to re-render once per minute. Pre-A5 this
  // role was filled by `forceTimeTick` + a per-column setInterval; that
  // setInterval is gone and the unused state slot has been removed.
  useSharedMinuteTimer();
  
  // Load time clock entries + realtime subscription (shop_profile fetch removed — L-3)
  useEffect(() => {
    const shopId = (technician as any).shop_id;
    if (!shopId) return;

    let didCancel = false;
    const channel: any = null;

    const loadEntries = async () => {
      const nowShop = toShopTZ(clock.now());
      const todayStr = format(nowShop, 'yyyy-MM-dd');

      const { data: entries, error } = await supabase
        .from('time_clock_entries')
        .select('*')
        .eq('technician_id', technician.id)
        .eq('shop_id', shopId)
        // M-4: Build day range in UTC using shop TZ — bare strings are server-TZ (UTC) and miss UTC+ shops
        .gte('clock_in_time', fromZonedTime(`${todayStr} 00:00`, shopTimezone).toISOString())
        .lte('clock_in_time', fromZonedTime(`${todayStr} 23:59`, shopTimezone).toISOString())
        .order('clock_in_time', { ascending: false });

      if (error) {
        console.warn('[TechColumn] time_clock_entries fetch failed:', error);
        return;
      }

      if (didCancel) return;

      if (entries && entries.length > 0) {
        const active = entries.find((e) => !e.clock_out_time);
        setActiveClockEntry(active || null);
        setLastClockEntryToday(entries[0]);
      } else {
        setActiveClockEntry(null);
        setLastClockEntryToday(null);
      }
    };

    if (!timeClockEnabled) {
      setActiveClockEntry(null);
      setLastClockEntryToday(null);
      return;
    }

    loadEntries();

    // Per-tech presence subscription removed — shared Dashboard timeclock channel handles this

    // TIM-A5: per-column 30s render-tick removed — driven now by the
    // module-level `useSharedMinuteTimer()` subscription at the top of
    // this component. One global 60s singleton replaces N per-column
    // 30s kernels; the subscription's React state update is what
    // notifies this column to re-render so `computePresenceLabel`
    // re-evaluates the `clock.now()`-derived label buckets.

    return () => {
      didCancel = true;
    };
  }, [technician.id, (technician as any).shop_id, toShopTZ, timeClockEnabled]);

  // Draggable for column reordering
  const { attributes, listeners, setNodeRef: setDragRef, isDragging: isColumnDragging } = useDraggable({
    id: technician.id,
    disabled: !isDraggable,
    data: {
      type: 'technician',
      technician: technician
    }
  });
  
  // Also make droppable for other technicians
  const { setNodeRef: setDropRefForTech, isOver: isOverForTech } = useDroppable({
    id: `tech-drop-${technician.id}`,
    data: {
      type: 'technician-drop',
      technicianId: technician.id
    }
  });
  
  // Check if we should show preview
  const showPreview = dragOverTechId === technician.id && previewStartTime && activeTicketId;
  const activeTicket = activeTicketProp;
  
// Phase C — header workload now uses the canonical derived-time record
// per ticket (sumWorkloadMinutes). Replaces the pre-Phase-C reducer that
// summed raw `predicted_minutes` regardless of state, which produced two
// pathologies:
//   1. Active 10+ hour run-long tickets reported only book time (e.g.
//      TKT-0257: 18 min book, 10:20 elapsed → header showed 65 min).
//   2. Held-in-parking tickets inflated the header as if bay-blocking.
//
// Per Q1 doctrine:
//   - Unstarted → techOptiTime (wall-time scheduling).
//   - Active running → techOptiTime + runLong (live projected occupancy).
//   - Paused in-bay → techOptiTime + bay-blocking pause time.
//   - Held-in-parking / non-bay → 0 (not bay/tech-blocking).
//   - Terminal → 0.
//
// NOV 3 (Decision 3, point 8): `liveElapsedSeconds` is the RECORDED WORK (headerElapsedFromEvidence: the work
// sessions, the server's own measure) — no longer (now − actual_start_time), which turned an overnight break into
// run-long (Adam F: 1,854). Unread sessions make the elapsed value unknown: the record then derives no pause and no
// live run-long (the server's persisted run-long still counts). Each ticket is bounded by ITS scheduled day's close,
// so a pause stops growing on the card's wall clock once that day has ended.
const _nowMs = canonicalClock.nowMs();
const _derivedTimes = (
  techTickets.map((t) => {
    const elapsed = headerElapsedFromEvidence(t as never, workEvidence(t.id), _nowMs);
    const liveElapsedSeconds = elapsed.seconds;
    const rowDay = String((t as any).scheduled_date ?? selectedDayLabel ?? '').slice(0, 10);
    let shiftEndUtc: Date | null = null;
    try {
      const close = /^\d{4}-\d{2}-\d{2}$/.test(rowDay) ? getTechDayHours(technician as never, rowDay)?.close : null;
      shiftEndUtc = close ? getTechShiftBoundaryUtc(rowDay, close, shopTimezone) : null;
    } catch { shiftEndUtc = null; }
    const startIso = (t as any).start_time as string | null | undefined;
    const startInstant = startIso ? new Date(startIso) : null;
    return getDerivedTicketTime({
      ticket: t as any,
      technician: {
        efficiencyPercentage: technician.efficiencyPercentage ?? 100,
        bufferPercentage: resolvePlanningBufferPct(technician.bufferPercentage),
        recurring_breaks: (technician as any).recurring_breaks ?? null,
        non_recurring_breaks: (technician as any).non_recurring_breaks ?? null,
        shift_start: (technician as any).shift_start ?? null,
        shift_end: (technician as any).shift_end ?? null,
      },
      jobs: t.jobs as any,
      shopConfig: { willingnessRollPastEodMin: 0, eodRiskBandMin: 30 },
      startInstant: startInstant && Number.isFinite(startInstant.getTime()) ? startInstant : null,
      shiftEndUtc,
      nowInstant: new Date(_nowMs),
      liveElapsedSeconds,
      liveElapsedKnown: elapsed.known,
      liveElapsedSampledAtMs: _nowMs,
    });
  })
);
const totalBookMinutes = sumWorkloadMinutes(_derivedTimes);
// W4b (W5 interactions): a multi-bay technician's header is the hands-on work that REMAINS — switched-away, unattended
// and parked time is not labor, work already done is not still to do. Single-bay columns keep the Phase C number.
// OCT28 WP-2b-2b-i: work done = the recorded work sessions (never wall time − the pause counter); a started ticket
// whose sessions are unread makes the total unknown ("—"), never a smaller number.
const remainingHandsOn = sumRemainingHandsOn(
  techTickets.map((t, i) => ({ ticket: t as never, techOptiTime: _derivedTimes[i]?.techOptiTime ?? 0, terminal: t.status !== 'Active' && t.status !== 'Paused', sessions: workEvidence(t.id) })),
  _nowMs,
);
const remainingHandsOnMinutes = remainingHandsOn.unknownCount > 0 ? '—' : remainingHandsOn.minutes;
  
  const activeCount = techTickets.filter(t => t.status === 'Active').length;

  // Compute smart presence label
  const computePresenceLabel = (): string => {
    const nowShop = toShopTZ(clock.now());
    const isToday = selectedDayLabel === format(nowShop, 'yyyy-MM-dd');

      // CRITICAL OVERRIDE: Running timer always means Present (even before shift)
      const hasRunningTicket = techTickets.some(t => 
        (t as any).timer_status === 'running'
      );
      if (hasRunningTicket) return 'Present';

    // Past/future dates (not today)
    if (!isToday) {
      return isTechnicianScheduledForDay(
        { hours: (technician as any).hours || {}, time_off: (technician as any).time_off || [] },
        selectedDate
      ) ? 'Scheduled' : 'Off';
    }

    // TODAY + Time Clock Enabled
    if (timeClockEnabled) {
      if (activeClockEntry) return 'Present';

      const isScheduledToday = isTechnicianScheduledForDay(
        { hours: (technician as any).hours || {}, time_off: (technician as any).time_off || [] },
        selectedDate
      );

      if (!isScheduledToday) return 'Off';

      if (!techHours || !techHours.open || !techHours.close) return 'Off';

      const shiftStart = parse(techHours.open, 'HH:mm', nowShop);
      const shiftEnd = parse(techHours.close, 'HH:mm', nowShop);

      if (nowShop < shiftStart) return 'Pre-Shift';
      if (nowShop > shiftEnd) return 'Off-Shift';

      // Mid-shift - check if they've clocked in at all today
      if (lastClockEntryToday?.clock_out_reason) {
        // They clocked out earlier - show that reason
        const reason = lastClockEntryToday.clock_out_reason;
        // Shorten "Parts Run" → "Parts", "End of Shift" → "Ended"
        if (reason === 'Parts Run') return 'Parts';
        if (reason === 'End of Shift') return 'Ended';
        return reason; // Lunch, Break, Meeting, Training as-is
      }
      // Bucket 5 §5.5.4 — Slice 0.3b: never clocked in today at all. Resolve
      // 'Awaiting' vs 'No-Show Risk' based on shop_culture.no_show_grace_
      // threshold_min. Display-only — does NOT mark absent, change is_present,
      // block timers, alter OptiAssign eligibility, or emit board issues.
      const currentMin = nowShop.getHours() * 60 + nowShop.getMinutes();
      const shiftStartMin = shiftStart.getHours() * 60 + shiftStart.getMinutes();
      return getTimeClockUnclockedLabel({
        currentShopMinutes: currentMin,
        shiftStartShopMinutes: shiftStartMin,
        thresholdMin: noShowGraceThresholdMin,
      });
    }

    // TODAY + Time Clock Disabled (schedule-inferred)
    // Check manual presence toggle first - if explicitly marked absent, show "Absent"
    if (technician.isPresent === false) return 'Absent';

    const isScheduledToday = isTechnicianScheduledForDay(
      { hours: (technician as any).hours || {}, time_off: (technician as any).time_off || [] },
      selectedDate
    );

    if (!isScheduledToday) return 'Off';

    if (!techHours || !techHours.open || !techHours.close) return 'Off';

    // Use minutes-based comparison to avoid timezone Date object issues
    const nowMinutes = nowShop.getHours() * 60 + nowShop.getMinutes();
    const [startH, startM] = techHours.open.split(':').map(Number);
    const [endH, endM] = techHours.close.split(':').map(Number);
    const shiftStartMinutes = startH * 60 + startM;
    const shiftEndMinutes = endH * 60 + endM;

    if (nowMinutes < shiftStartMinutes) return 'Pre-Shift';
    if (nowMinutes > shiftEndMinutes) return 'Off-Shift';

    // Check break windows
    const currentMinutes = nowShop.getHours() * 60 + nowShop.getMinutes();

    // Recurring breaks
    for (const br of ((technician as any).recurring_breaks || [])) {
      if (br.start && br.end) {
        const [startH, startM] = br.start.split(':').map(Number);
        const [endH, endM] = br.end.split(':').map(Number);
        const breakStart = startH * 60 + startM;
        const breakEnd = endH * 60 + endM;

        if (currentMinutes >= breakStart && currentMinutes <= breakEnd) {
          return br.name || 'Break';
        }
      }
    }

    // Non-recurring breaks for today
    const dateStr = selectedDayLabel;
    for (const br of ((technician as any).non_recurring_breaks || [])) {
      if (br.date === dateStr && br.start && br.end) {
        const [startH, startM] = br.start.split(':').map(Number);
        const [endH, endM] = br.end.split(':').map(Number);
        const breakStart = startH * 60 + startM;
        const breakEnd = endH * 60 + endM;

        if (currentMinutes >= breakStart && currentMinutes <= breakEnd) {
          return br.name || 'Break';
        }
      }
    }

    return 'Present';
  };

  const presenceLabel = computePresenceLabel();
  const isPresent = presenceLabel === 'Present' || presenceLabel === 'Scheduled';

  // Calculate utilization percentage for the selected date
  const calculateUtilization = () => {
    // Same canonical resolver as the header, so the percentage and the hours
    // printed above it can never be computed from two different days.
    const techHours = getTechDayHours(technician as never, selectedDayLabel);

    if (!techHours || !techHours.open || !techHours.close) return 0;
    
    // Parse shift hours
    const [startHour, startMin] = techHours.open.split(':').map(Number);
    const [endHour, endMin] = techHours.close.split(':').map(Number);
    const shiftMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    
    // Calculate breaks
    const recurringBreaks = (technician as any).recurring_breaks || [];
    const nonRecurringBreaks = (technician as any).non_recurring_breaks || [];
    const dateStr = selectedDayLabel;
    const breakMinutes = [...recurringBreaks, ...nonRecurringBreaks].reduce((sum: number, br: any) => {
      // Only count non-recurring breaks that match the selected date
      if (br.date && br.date !== dateStr) return sum;
      if (br.start && br.end) {
        const [bStartH, bStartM] = br.start.split(':').map(Number);
        const [bEndH, bEndM] = br.end.split(':').map(Number);
        return sum + ((bEndH * 60 + bEndM) - (bStartH * 60 + bStartM));
      }
      return sum;
    }, 0);
    
    const availableMinutes = shiftMinutes - breakMinutes;
    
// B-W4 rule 5: totalBookMinutes is OptiTime WORKLOAD (sumWorkloadMinutes: book ÷ efficiency × (1 + buffer)), so it
// already carries efficiency and buffer. The capacity side used to apply both again (× efficiency × (1 − buffer)),
// counting the buffer twice. Wall minutes ÷ scheduled wall minutes — the same 'scheduled' basis as the load index.
const capacityWall = availableMinutes;

if (capacityWall <= 0) return 0;

return Math.min(100, (totalBookMinutes / capacityWall) * 100);
  };

  const utilization = calculateUtilization();
  
  const getUtilizationColor = (util: number) => {
    if (util >= 100) return 'bg-red-900';
    if (util >= 90) return 'bg-red-500';
    if (util >= 60) return 'bg-orange-500';
    if (util >= 30) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Droppable for tickets
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: technician.id,
    data: {
      type: 'ticket-drop',
      technicianId: technician.id
    }
  });

  const ticketIds = multiBay.active ? multiBaySortableIds(techTickets, multiBay) : techTickets.map(t => t.id);
  
  // W5: the per-ticket row, extracted VERBATIM so the single-bay path and the multi-bay sections render the same
  // TicketCard (one sortable id per ticket, original index kept for the ghost preview and cascade shift).
  const renderTicketRow = (ticket: Ticket, idx: number) => {
    // Determine if this ticket should show cascade effect
    const shouldCascade = showPreview && hoverIndex >= 0 && idx >= hoverIndex && (ticket as any).type !== 'Waiter';
    
    return (
      <div key={ticket.id}>
        {/* Show preview ghost ticket at insertion point */}
        {showPreview && hoverIndex === idx && activeTicket && (
          <div className="mb-3 opacity-60 border-2 border-dashed border-primary rounded-lg p-3 bg-primary/5 animate-pulse">
            <div className="text-sm font-medium">{(activeTicket as any).customerName || 'Dropping here...'}</div>
            <div className="text-xs text-muted-foreground mt-1">
              <div>Start: {formatTimeInShopTZ(new Date(previewStartTime!))}</div>
              {activeTicket.predictedMinutes && (
                <div>
                  Finish: {formatTimeInShopTZ(new Date(
                    new Date(previewStartTime!).getTime() + 
calculateWallMinutes(
                      activeTicket.predictedMinutes || 0,
                      technician.efficiencyPercentage || 100,
                      resolvePlanningBufferPct(technician.bufferPercentage)
                    ) * 60000
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Actual ticket with cascade animation */}
        <div 
          className={cn(
            "transition-all duration-300 ease-in-out mb-3",
            shouldCascade && "translate-y-20 opacity-60"
          )}
        >
          <TicketCard 
            ticket={ticket} 
            isDraggable={isDraggable && !readOnly}
            technicians={(ticket as any).technicians || []}
            technician={{
              name: technician.name,
              efficiencyPercentage: technician.efficiencyPercentage,
              // CORE-AC: the badge beside the OptiTime must describe
              // the SAME efficiency the OptiTime used.
              efficiencySource: technician.efficiencySource,
              efficiencySampleSize: technician.efficiencySampleSize,
              bufferPercentage: technician.bufferPercentage,
              shiftStart: technician.shiftStart,
              shiftEnd: technician.shiftEnd,
            }}
            compactView={compactView}
            isHighlighted={highlightedTicketId === ticket.id}
            isPendingMove={pendingMoveTicketId === ticket.id}
            onReassign={readOnly ? undefined : onKebabReassign}
            isNewOptiQueue={newOptiQueueTicketIds.has(ticket.id)}
            hideKebabMenu={readOnly}
            hideTimerControls={readOnly}
            parkingSpotLabel={(ticket as any).parking_spot?.label}
            advisorBayHoldData={advisorBayHoldByTicketId?.get(ticket.id) ?? null}
            splitLabelData={splitLabelDataByTicketId?.get(ticket.id) ?? null}
            onTicketUpdated={() => onTicketUpdated?.(ticket.id)}
          />
        </div>
      </div>
    );
  };

  const handleTogglePresence = async () => {
    // Only allow manual toggle when time clock is disabled
    if (timeClockEnabled) return;

    const newPresence = !technician.isPresent;
    
    // Update technician presence first (so smart-assign sees the new state)
    const { error } = await supabase
      .from('technicians')
      .update({ is_present: newPresence })
      .eq('id', technician.id);
    
    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to update technician presence',
        variant: 'destructive',
      });
      return;
    }
    
    // Optimistically update UI
    toast({
      title: newPresence ? 'Marked Present' : 'Marked Absent',
      description: newPresence 
        ? `${technician.name} is now marked as present` 
        : `${technician.name} is now marked as absent`,
    });
    
    // If marking absent, hand ALL scheduled tickets (not just Active) to the Dashboard, which opens a review where a
    // person confirms each move (WS-ABSENT: nothing is reassigned automatically — CORE-AB:43, CORE-AD:80).
    if (!newPresence && techTickets.length > 0 && onReassignNeeded) {
      onReassignNeeded(techTickets, technician.name);
    }
  };

  const combineRefs = (...refs: any[]) => {
    return (node: any) => {
      refs.forEach(ref => {
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref != null) {
          ref.current = node;
        }
      });
    };
  };

  return (
    <div 
      ref={combineRefs(setDragRef, setDropRefForTech)}
      data-testid={`technician-column-${technician.id}`}
      className={cn(
        "flex flex-col border-r bg-card last:border-r-0 self-start min-h-full transition-all duration-200",
        "w-full md:w-80 md:min-w-[320px] md:max-w-[400px]",
        isColumnDragging && "opacity-50",
        isOverForTech && "ring-2 ring-primary"
      )}
    >
      {/* Header */}
      <div className="p-4 border-b bg-muted/30 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {isDraggable && (
              <div 
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing touch-none"
              >
                <GripVertical className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
              </div>
            )}
            <div>
              <h3 
                className="font-semibold text-sm cursor-pointer hover:text-primary hover:underline transition-colors"
                onClick={async () => {
                  const profileId = (technician as any).technician_profile_id;
                  if (profileId) {
                    navigate(`/technicians/${profileId}`);
                    return;
                  }
                  // Self-view shortcut
                  if ((technician as any).user_id === user?.id) {
                    navigate('/passport');
                    return;
                  }
                  // Fallback: look up linked user and their technician profile
                  const { data: techUserLink } = await supabase
                    .from('technician_users')
                    .select('user_id')
                    .eq('technician_id', technician.id)
                    .maybeSingle();
                  if (techUserLink?.user_id) {
                    if (techUserLink.user_id === user?.id) {
                      navigate('/passport');
                      return;
                    }
                    // For managers: find the technician_profile by user_id
                    if (isManagerView) {
                      const { data: profile } = await supabase
                        .from('technician_profiles')
                        .select('id')
                        .eq('user_id', techUserLink.user_id)
                        .maybeSingle();
                      if (profile?.id) {
                        navigate(`/technicians/${profile.id}`);
                        return;
                      }
                    }
                  }
                  // Last resort for managers: tech edit page
                  if (isManagerView) {
                    navigate(`/technicians/${technician.id}/edit`);
                  }
                }}
                data-testid={isFirstTechnician ? 'technician-passport-link' : undefined}
              >
                {technician.name}
              </h3>
              <p className="text-xs text-muted-foreground">{technician.role}</p>
            </div>
            {/* Codex: PausedWorkBadge — role-gated to advisor/manager/owner */}
            {canSeePausedBadge && pausedWorkflowTickets.length > 0 && (
              <PausedWorkBadge pausedTickets={pausedWorkflowTickets} />
            )}
          </div>
          <Badge
            variant={isPresent ? 'default' : 'destructive'}
            className="h-6 text-xs px-2 cursor-pointer"
            onClick={timeClockEnabled ? undefined : handleTogglePresence}
          >
            {presenceLabel}
          </Badge>
        </div>
        
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>
            {(() => {
              // A column that names a DATE must show that date's hours.
              if (techHours) {
                return `${formatTimeString(techHours.open, 'short')} - ${formatTimeString(techHours.close, 'short')}`;
              }
              // No per-day entry for this date. The flat columns are a
              // `typical_week` summary (see `technicianShiftEnvelope`), so
              // they are LABELLED rather than printed as if they were today's
              // schedule — the same ruling Shop Profile got in Handoff 01,
              // applied here only where the canonical map has no answer.
              if (technician.shiftStart && technician.shiftEnd) {
                return `Typical ${formatTimeString(technician.shiftStart, 'short')} - ${formatTimeString(technician.shiftEnd, 'short')}`;
              }
              return 'No hours set';
            })()}
          </span>
        </div>

        {/* Breaks - only for selected date */}
        {((technician as any).recurring_breaks?.length > 0 || (technician as any).non_recurring_breaks?.length > 0) && (
          <div className="mt-2 space-y-1">
            {(technician as any).recurring_breaks?.map((br: any, idx: number) => {
              return (
                <div key={`rec-${idx}`} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>🍽️</span>
                  <span>{formatTimeString(br.start, 'short')} - {formatTimeString(br.end, 'short')}</span>
                </div>
              );
            })}
            {(technician as any).non_recurring_breaks?.map((br: any, idx: number) => {
              const dateStr = selectedDayLabel;
              if (br.date === dateStr) {
                return (
                  <div key={`non-${idx}`} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>☕</span>
                    <span>{formatTimeString(br.start, 'short')} - {formatTimeString(br.end, 'short')}</span>
                  </div>
                );
              }
              return null;
            })}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
          <div>
            <span className="text-muted-foreground" title={EFFICIENCY_NAMES.planning.scope}>{EFFICIENCY_NAMES.planning.label}:</span>
            <span className="ml-1 font-medium">{technician.efficiencyPercentage || 100}%</span>
          </div>
          <div>
            <span className="text-muted-foreground">Buffer:</span>
            <span className="ml-1 font-medium">{formatPlanningBufferLabel(technician.bufferPercentage)}</span>
          </div>
        </div>

        {((technician as any).bay_assignments?.length > 0) && (
          <div className="flex flex-wrap gap-1 mt-2">
            {((technician as any).bay_assignments || []).map((bay: string, idx: number) => (
              <Badge key={idx} variant="secondary" className="text-xs font-medium">
                {bay}
              </Badge>
            ))}
          </div>
        )}

        <div className="mt-2 pt-2 border-t flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Workload:</span>
            <div 
              className={cn("h-4 w-4 rounded-full cursor-help", getUtilizationColor(utilization))}
              title={`${Math.round(utilization)}% utilized`}
            />
          </div>
          <span className="font-medium" title={multiBay.active && remainingHandsOn.unknownCount > 0 ? `Recorded work could not be read for ${remainingHandsOn.unknownCount} started repair(s)` : undefined}>{multiBay.active ? remainingHandsOnMinutes : totalBookMinutes} min ({activeCount} active)</span>
        </div>
        {multiBay.active && (
          <p className="mt-1 text-xs text-muted-foreground" data-testid={`multibay-overlap-${technician.id}`}>
            Working in {multiBay.distinctBays} bays · {Array.from(new Set(multiBay.vehicles.map(v => v.bayId))).map(id => bayNames?.get(id) ?? 'Bay').join(', ')}
          </p>
        )}
      </div>

      {/* Oct 20 field test, Issue 10: "View {tech}'s Bays" sits directly under
          the schedule header, above the first ticket, so it is found before
          the column's tickets rather than after them. Same condition
          (`multiBay.active`, which is never true alongside the empty state),
          same button, same dialog. It stays outside the drop zone and the
          SortableContext, as before, so drag and drop is unaffected; a
          single-bay column renders nothing here (W5 goldens unchanged). */}
      {multiBay.active && (
        <div className="px-3 pt-3 bg-background/50" data-testid={`view-tech-bays-bar-${technician.id}`}>
          <Button
            variant="outline"
            size="sm"
            className="w-full min-h-11 md:min-h-9"
            aria-haspopup="dialog"
            data-testid={`view-tech-bays-${technician.id}`}
            onClick={() => setWorkspaceOpen(true)}
          >
            View {technician.name}&apos;s Bays
          </Button>
        </div>
      )}

      {/* Tickets */}
      <div 
        ref={setDropRef}
        className={cn(
          "flex-1 p-3 space-y-3 bg-background/50 transition-colors min-h-[200px]",
          isOver && "bg-primary/10 ring-2 ring-primary ring-inset"
        )}
      >
        <SortableContext items={ticketIds} strategy={verticalListSortingStrategy}>
          {techTickets.length === 0 && !showPreview ? (
            <div className="rounded-xl border bg-card p-6 text-center text-muted-foreground">
              <Wrench className="h-8 w-8 mb-2 opacity-50 mx-auto" />
              <p className="text-sm">No tickets assigned</p>
            </div>
          ) : (
            <>
              {multiBay.active ? (
                <MultiBayTechnicianSections
                  technicianId={technician.id}
                  techTickets={techTickets}
                  activity={multiBay}
                  renderTicketRow={renderTicketRow}
                  bayNameOf={(id) => bayNames?.get(id) ?? null}
                  formatTime={(d) => formatTimeInShopTZ(d)}
                />
              ) : techTickets.map(renderTicketRow)}
              
              {/* Show preview at end if hovering at end */}
              {showPreview && activeTicket && hoverIndex === techTickets.length && (
                <div className="opacity-60 border-2 border-dashed border-primary rounded-lg p-3 bg-primary/5 animate-pulse">
                  <div className="text-sm font-medium">{(activeTicket as any).customerName || 'Dropping here...'}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    <div>Start: {formatTimeInShopTZ(new Date(previewStartTime!))}</div>
                    {activeTicket.predictedMinutes && (
                      <div>
                        Finish: {formatTimeInShopTZ(new Date(
                          new Date(previewStartTime!).getTime() + 
calculateWallMinutes(
                             activeTicket.predictedMinutes || 0,
                             technician.efficiencyPercentage || 100,
                             resolvePlanningBufferPct(technician.bufferPercentage)
                           ) * 60000
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </SortableContext>
      </div>
      {workspaceOpen && (
        <TechnicianWorkspaceDialog
          open={workspaceOpen}
          onOpenChange={setWorkspaceOpen}
          technician={{ id: technician.id, name: technician.name, shop_id: (technician as any).shop_id ?? null }}
          dayLabel={selectedDayLabel}
          canAct={!readOnly}
          canEndAnyOperation={!readOnly && (role === 'OWNER' || role === 'MANAGER')}
        />
      )}
    </div>
  );
};
